import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { Project } from '@/types';

interface ProjectsState {
  // State
  projects: Project[];
  currentProjectId: string | null;
  isProjectsLoading: boolean;
  hasLoadedProjects: boolean;
  projectsPromise: Promise<void> | null;
  // Actions
  setProjects: (projects: Project[]) => void;
  setCurrentProjectId: (projectId: string | null) => void;
  setIsProjectsLoading: (isLoading: boolean) => void;
  setHasLoadedProjects: (hasLoaded: boolean) => void;
  setProjectsPromise: (promise: Promise<void> | null) => void;
  addProject: (project: Project) => void;
  removeProject: (projectId: string) => void;
  updateProject: (projectId: string, updates: Partial<Project>) => void;
  getCurrentProject: () => Project | undefined;
}
export const useProjectsStore = create<ProjectsState>()(
  devtools(
    (set, get) => ({
      // Initial state
      projects: [],
      currentProjectId: null,
      isProjectsLoading: false,
      hasLoadedProjects: false,
      projectsPromise: null,
      // Actions
      setProjects: projects => set({ projects }),
      setCurrentProjectId: projectId => set({ currentProjectId: projectId }),
      setIsProjectsLoading: isLoading => set({ isProjectsLoading: isLoading }),
      setHasLoadedProjects: hasLoaded => set({ hasLoadedProjects: hasLoaded }),
      setProjectsPromise: promise => set({ projectsPromise: promise }),
      addProject: project =>
        set(state => ({ projects: [...state.projects, project] })),
      removeProject: (projectId: string) =>
        set(state => ({
          projects: state.projects.filter(p => p.id !== projectId),
          currentProjectId:
            state.currentProjectId === projectId
              ? null
              : state.currentProjectId,
        })),
      updateProject: (projectId, updates) =>
        set(state => ({
          projects: state.projects.map(p =>
            p.id === projectId ? { ...p, ...updates } : p
          ),
        })),
      getCurrentProject: () => {
        const state = get();
        return state.projects.find(p => p.id === state.currentProjectId);
      },
    }),
    { name: 'ProjectsStore' }
  )
);
